import { Avatar, Box, HStack, Text, VStack } from "@chakra-ui/react";
import {
  CalendarCheck,
  ChevronDown,
  CircleHelp,
  Download,
  History,
  LogOut,
} from "lucide-react";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import useAbilityPermissions from "../../hooks/useAbilityPermissions";
import { updateAppStatus } from "../../store/actions/authActions";
import { updateDrawer } from "../../store/actions/miscAction";
import { APP_STATUSES } from "../../store/constants";

const MenuItem = ({ icon: Icon, children, onClick }) => (
  <Box
    as="button"
    type="button"
    w="100%"
    fontSize="13px"
    py="7px"
    px="10px"
    borderRadius="4px"
    cursor="pointer"
    transition="background 0.15s"
    _hover={{ bg: "#f4f4f5" }}
    onClick={onClick}
    color="#09090b"
    bg="transparent"
    border="0"
    textAlign="left"
  >
    <HStack gap="8px" justify="space-between">
      <HStack gap="8px">
        {Icon && <Icon size={14} color="var(--gray)" />}
        <Text as="span" fontSize="13px" color="#09090b">
          {children}
        </Text>
      </HStack>
    </HStack>
  </Box>
);

const MenuDivider = () => <Box h="1px" bg="#e4e4e7" my="4px" />;

function UserProfileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAbilityPermissions();
  const { userData } = useSelector((store) => store.authReducer);
  const { name = "", lastname = "", email = "" } = userData || {};
  const fullName = `${name} ${lastname}`.trim() || "User";
  const displayName = name || fullName;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }

    return undefined;
  }, [isOpen]);

  const goTo = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const openDrawer = () => {
    setIsOpen(false);
    dispatch(
      updateDrawer({
        title: "Download Past reports",
        isOpen: 1,
        content: { component: "PastReportsForm" },
      })
    );
  };

  const logout = () => {
    Cookies.remove("accessToken");
    Cookies.remove("userId");
    dispatch(updateAppStatus(APP_STATUSES.LOGGED_OUT));
    window.location.replace(`https://auth.averly.com.na?origin=${window.location.href}`);
  };

  return (
    <Box position="relative" ref={menuRef}>
      {isOpen && (
        <Box
          position="absolute"
          bottom="100%"
          left="10px"
          right="10px"
          mb="6px"
          bg="white"
          border="1px solid"
          borderColor="#e4e4e7"
          borderRadius="8px"
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.12)"
          p="6px"
          zIndex="1100"
          w="250px"
        >
          <Box px="10px" py="7px" borderBottom="1px solid" borderColor="#e4e4e7" mb="4px">
            <Text fontSize="12px" color="#71717a" truncate>
              {email || "No email"}
            </Text>
          </Box>

          {isSuperAdmin && (
            <MenuItem icon={History} onClick={() => goTo("/updates")}>
              Updates
            </MenuItem>
          )}
          <MenuItem icon={CircleHelp} onClick={() => goTo("/help")}>
            Get help
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={CalendarCheck} onClick={() => goTo("/reports/subscriptions")}>
            Report Subscriptions
          </MenuItem>
          <MenuItem icon={Download} onClick={openDrawer}>
            Download Past Reports
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={LogOut} onClick={logout}>
            Log out
          </MenuItem>
        </Box>
      )}

      <Box
        as="button"
        type="button"
        w="100%"
        cursor="pointer"
        py="10px"
        bg="transparent"
        border="0"
        borderTop="1px solid #e4e4e7"
        transition="all 0.15s"
        _hover={{ bg: "#f4f4f5" }}
        onClick={() => setIsOpen(!isOpen)}
        textAlign="left"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <HStack gap="10px" align="center" px="12px">
          <Avatar.Root size="sm" bg="var(--black)" color="white">
            <Avatar.Fallback name={fullName} />
          </Avatar.Root>
          <VStack align="flex-start" gap="1px" flex="1" minW="0">
            <Text
              fontSize="13px"
              fontWeight="600"
              color="#09090b"
              lineHeight="1.3"
              truncate
            >
              {displayName}
            </Text>
            <Text fontSize="11px" color="#71717a" lineHeight="1.3" truncate>
              {email || "No email"}
            </Text>
          </VStack>
          <ChevronDown size={14} color="var(--gray)" />
        </HStack>
      </Box>
    </Box>
  );
}

export default UserProfileMenu;
